import React, { useState, useRef, useEffect, MutableRefObject, ClipboardEvent, KeyboardEvent, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { FlosFormGroup } from './form-group/flos-form-group';
import { getId } from '../../utils';
import { callAll } from '../../utils/fp';

import type { FlosInputProps } from './input/flos-input';

type FieldProps<T> = T;

type MarkFieldDirtyOptions = {
    isDirty?: boolean;
};

type FlosFieldRenderInputProps = {
    getInputProps: <T>(oriProps: FieldProps<T>) => FieldProps<T>;
    markFieldDirty: (value?: string, options?: MarkFieldDirtyOptions) => void;
};

type ValidationStatus = 'pristine' | 'valid' | 'invalid';
type ValidationReason = 'initial' | 'input' | 'blur' | 'commit' | 'reset' | 'external' | 'local';
type ValidationInteraction = 'idle' | 'keyboard' | 'pointer' | 'programmatic';

type ValidationState = {
    status: ValidationStatus;
    reason: ValidationReason;
    dirty: boolean | undefined;
    interaction?: ValidationInteraction;
};

type FlosFieldInternalProps = {
    renderInput: (renderInputProps: FlosFieldRenderInputProps) => JSX.Element;
    hasDropdown?: boolean;
    readOnlyHandler?: () => void | null;
};

type FlosFieldProps = {
    label?: string;
    wrapperClassName?: string;
    iconShape?: string;
    isValid?: boolean;
    isDirty?: boolean;
    errorText?: string;
    helpText?: string;
    required?: boolean;
    pattern?: string;
    maxLength?: number;
    minLength?: number;
    id?: string;
    disabled?: boolean;
    custom?: boolean;
    editable?: boolean;
    onValidityChange?: (isValid: boolean | undefined) => void;
    validationState?: ValidationState;
    onValidationStateChange?: (next: ValidationState) => void;
    resetHandler?: () => void;
    showLength?: boolean;
    isOpen?: boolean;
    variant?: 'default' | 'typeahead';
};

type IconType = null | 'arrow' | 'close';

type ChevronTypeArgs = {
    variant: 'default' | 'typeahead';
    hasDropdown: boolean;
    disabled?: boolean;
    resetHandler?: () => void;
    isDirty?: boolean;
    editable?: boolean;
    hasValue?: boolean;
};

// Helper functions
const getChevronType = ({ variant, hasDropdown, disabled, resetHandler, isDirty, editable, hasValue }: ChevronTypeArgs): IconType => {
    if (variant === 'typeahead') {
        const hasNonEmptyValue = Boolean(hasValue);
        if (!hasDropdown || disabled) return null;
        if (resetHandler && isDirty && hasNonEmptyValue) {
            return 'close';
        }
        return 'arrow';
    }
    return !editable && hasDropdown && !disabled ? 'arrow' : null;
};

const statusFromBoolean = (value: boolean | undefined): ValidationStatus => {
    if (value === true) return 'valid';
    if (value === false) return 'invalid';
    return 'pristine';
};

const booleanFromStatus = (status: ValidationStatus): boolean | undefined => {
    if (status === 'valid') return true;
    if (status === 'invalid') return false;
    return undefined;
};

const FlosField = React.forwardRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, FlosFieldProps & FlosFieldInternalProps>(
    (
        {
            custom,
            disabled,
            editable,
            errorText,
            hasDropdown = false,
            helpText,
            iconShape,
            id,
            isValid: propIsValid,
            isDirty: externalIsDirty,
            label,
            maxLength = 0,
            minLength = 0,
            onValidityChange,
            validationState,
            onValidationStateChange,
            renderInput,
            resetHandler,
            required,
            wrapperClassName,
            showLength = true,
            isOpen,
            variant = 'default',
            ...rest
        },
        ref: React.RefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>
    ) => {
        let inputRefInternal = useRef() as MutableRefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
        if (ref) inputRefInternal = ref as MutableRefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

        const initialValueLength = inputRefInternal.current?.value?.length || 0;
        const isTypeaheadVariant = variant === 'typeahead';

        // Separate state variables for better performance
        const [valueLength, setValueLength] = useState(initialValueLength);
        const [isDirty, setIsDirty] = useState(!!initialValueLength);
        const [isValid, setIsValid] = useState(propIsValid);
        const [isReadOnly, setIsReadOnly] = useState(!!editable);
        const inputValueRef = useRef('');

        const derivedValidationState = useMemo<ValidationState>(() => {
            if (isTypeaheadVariant) {
                if (validationState) {
                    return validationState;
                }
                const fallbackDirty = typeof externalIsDirty !== 'undefined' ? externalIsDirty : isDirty;
                return {
                    status: statusFromBoolean(propIsValid ?? isValid),
                    reason: propIsValid !== undefined ? 'external' : 'local',
                    dirty: fallbackDirty,
                    interaction: 'programmatic',
                };
            }
            return {
                status: statusFromBoolean(isValid),
                reason: propIsValid !== undefined ? 'external' : 'local',
                dirty: isDirty,
                interaction: 'programmatic',
            };
        }, [externalIsDirty, isDirty, isTypeaheadVariant, isValid, propIsValid, validationState]);

        const effectiveIsValid = useMemo(() => booleanFromStatus(derivedValidationState.status), [derivedValidationState.status]);
        const effectiveIsDirty = useMemo(() => derivedValidationState.dirty, [derivedValidationState.dirty]);

        const inputId = id || getId();
        const formgroupRef = useRef<HTMLDivElement | null>(null);
        const hintText = effectiveIsValid === false ? errorText : helpText;

        const hasHintText = !!hintText;
        const hasLengthCounter = maxLength >= 1;
        const showHelptext = hasHintText || hasLengthCounter;
        const shouldShowHelptextRow = showHelptext;
        const shouldShowLengthCounter = showLength && hasLengthCounter;

        const determineIsValid = useCallback((currentIsDirty: boolean, valueLength: number, minLength: number, maxLength: number | undefined, propIsValid: boolean | undefined) => {
            if (typeof propIsValid !== 'undefined') return propIsValid;
            if (currentIsDirty && valueLength !== 0) {
                return valueLength >= minLength && (maxLength ? valueLength <= maxLength : true);
            }
            return undefined;
        }, []);

        // Event handlers
        const handleReadOnlyState = () => {
            setIsReadOnly((prev) => !prev);
        };

        const handleBlur = useCallback(
            (event?: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
                if (isTypeaheadVariant) {
                    if (event) {
                        const relatedTarget = event.relatedTarget as HTMLElement | null;
                        if (relatedTarget && formgroupRef.current?.contains(relatedTarget)) {
                            return;
                        }
                    }
                    setIsReadOnly(!!editable);
                    return;
                }
                const newIsValid = determineIsValid(isDirty, valueLength, minLength, maxLength, propIsValid);
                setIsValid(newIsValid);
                setIsReadOnly(!!editable);
            },
            [determineIsValid, editable, formgroupRef, isDirty, isTypeaheadVariant, maxLength, minLength, propIsValid, valueLength]
        );

        const markFieldDirty = useCallback(
            (value?: string, options?: MarkFieldDirtyOptions) => {
                if (typeof value === 'string') {
                    setValueLength(value.length);
                }

                if (typeof options?.isDirty !== 'undefined') {
                    setIsDirty(options.isDirty);
                    return;
                }

                if (isTypeaheadVariant) {
                    setIsDirty(value !== '' && value !== undefined);
                } else {
                    setIsDirty(true);
                }
            },
            [isTypeaheadVariant]
        );

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            event.stopPropagation();
            markFieldDirty(event.target.value);
        };

        const handleReset = () => {
            resetHandler?.();
            setIsDirty(false);
            setIsValid(undefined);
        };

        const handleResetKeyboard = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.code === 'Space' || e.code === 'Enter') {
                e.preventDefault();
                handleReset();
            }
        };

        const regExTest = useCallback((regex: RegExp, key: string) => regex.test(key), []);

        const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.currentTarget?.pattern) {
                const pattern = typeof event.currentTarget.pattern === 'string' ? new RegExp(event.currentTarget.pattern) : event.currentTarget.pattern;

                if (!regExTest(pattern, event.key)) event.preventDefault();
            }

            if (maxLength) {
                const ss = event.currentTarget.selectionStart || 0;
                const se = event.currentTarget.selectionEnd || 0;
                const selectionLength = se - ss;
                if (valueLength + 1 - selectionLength > maxLength) event.preventDefault();
            }
        };

        const handleKeyUp = () => {
            setIsDirty(true);
        };

        const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape' && inputRefInternal.current) {
                inputRefInternal.current.blur();
            }
        };

        const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
            const pasted = event.clipboardData.getData('text');
            if (!maxLength) return;

            const ss = event.currentTarget.selectionStart || 0;
            const se = event.currentTarget.selectionEnd || 0;
            const selectionLength = se - ss;

            if (valueLength + pasted.length - selectionLength > maxLength) {
                event.preventDefault();
            }
        };

        // Helper functions
        const getHelpTextId = () => `${inputId}-error`;
        const getAriaDescribedBy = (oriDescribedBy?: string) => (oriDescribedBy ? getHelpTextId() : undefined);

        const getInputProps = <T extends FlosInputProps>(oriProps: T) => {
            const baseHandlers = {
                onBlur: callAll(oriProps.onBlur, handleBlur),
                onFocus: callAll(oriProps.onFocus),
                onChange: callAll(oriProps.onChange, handleChange),
                onKeyPress: callAll(oriProps.onKeyPress, handleKeyPress),
                onPaste: callAll(oriProps.onPaste, handlePaste),
            };

            inputValueRef.current = typeof oriProps.value === 'string' ? oriProps.value : inputRefInternal.current?.value ?? inputValueRef.current;

            const baseProps = {
                'aria-describedby': getAriaDescribedBy(oriProps['aria-describedby']),
                id: inputId,
                ref: inputRefInternal,
                ...baseHandlers,
            };

            const variantHandlers = {
                default: {
                    onKeyUp: callAll(oriProps.onKeyUp, handleKeyUp),
                    onKeyDown: callAll(oriProps.onKeyDown, handleKeydown),
                    readOnly: typeof editable !== 'undefined' ? isReadOnly : oriProps.readOnly,
                },
                typeahead: {
                    onKeyUp: oriProps.onKeyUp,
                    onKeyDown: oriProps.onKeyDown,
                },
            };

            return {
                ...oriProps,
                ...baseProps,
                ...variantHandlers[variant],
            };
        };

        // Effects
        useEffect(() => {
            const currentLength = inputRefInternal.current?.value?.length || 0;
            setValueLength(currentLength);
        }, [inputRefInternal.current?.value]);

        useEffect(() => {
            setIsValid(propIsValid);
        }, [propIsValid]);

        useEffect(() => {
            onValidityChange?.(effectiveIsValid);
        }, [effectiveIsValid, onValidityChange]);

        useEffect(() => {
            setIsDirty(!!externalIsDirty);
        }, [externalIsDirty]);

        useEffect(() => {
            onValidationStateChange?.(derivedValidationState);
        }, [derivedValidationState, onValidationStateChange]);

        useEffect(() => {
            if (inputRefInternal.current && !isReadOnly && editable && variant === 'default') {
                inputRefInternal.current.focus();
            }
        }, [isReadOnly, editable, variant]);

        // Render helper functions
        const renderLabelIcons = () => {
            if (custom) return null;

            const hasInputValue = variant === 'typeahead' ? inputValueRef.current.trim() !== '' : valueLength > 0;
            const chevron = getChevronType({
                variant,
                hasDropdown,
                disabled,
                resetHandler,
                isDirty: effectiveIsDirty,
                editable,
                hasValue: hasInputValue,
            });

            const flosIconClass = cx('flos-field-icon', {
                'flos-field-icon--with-label': !!label,
            });

            return (
                <>
                    {label && (
                        <label htmlFor={inputId} className="flos-label">
                            {label}
                            {required && '*'}
                        </label>
                    )}

                    {iconShape && <flos-icon class={cx(flosIconClass, 'is-indigo')} role="img" shape={iconShape} aria-label={iconShape} />}

                    {chevron === 'arrow' && (
                        <flos-icon
                            class={cx(flosIconClass, 'flos-field-icon--secondary is-logo-blue', {
                                'flos-field-icon--reversed': isOpen,
                            })}
                            aria-label="arrow-down"
                            shape="arrow-down"
                            role="img"
                        />
                    )}

                    {chevron === 'close' && (
                        <flos-icon
                            onClick={handleReset}
                            onKeyDown={handleResetKeyboard}
                            class={cx(flosIconClass, 'flos-field-icon-reset flos-field-icon--secondary is-logo-blue')}
                            role="button"
                            aria-label="reset"
                            aria-description="Nulstil"
                            shape="close"
                            tabIndex={0}
                        />
                    )}

                    {editable && variant === 'default' && !chevron && (
                        <button className="flos-field-edit-button" tabIndex={0} onClick={handleReadOnlyState}>
                            <flos-icon class="is-interactive is-interactive--filled" shape="edit" aria-label="edit" />
                        </button>
                    )}
                </>
            );
        };

        const renderHelperText = () => {
            if (!shouldShowHelptextRow) return null;

            return (
                <div className="flos-field-helptext-row">
                    {hasHintText ? (
                        <div
                            className={cx('flos-field-helptext-col', {
                                'flos-field-helptext--valid': !disabled && effectiveIsValid,
                                'flos-field-helptext--invalid': !disabled && effectiveIsValid === false,
                            })}
                        >
                            {hintText}
                        </div>
                    ) : (
                        <div className="flos-field-helptext-col" />
                    )}

                    {shouldShowLengthCounter && (
                        <div className="flos-field-helptext-col flos-field-helptext-col--right">
                            {valueLength} / {maxLength}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <FlosFormGroup
                className={cx(
                    'flos-field',
                    {
                        'has-icon': iconShape,
                        'has-edit-button': editable && isReadOnly && variant === 'default',
                        'flos-field--with-helptext': shouldShowHelptextRow,
                        'has-chevron': hasDropdown,
                    },
                    wrapperClassName
                )}
                isValid={disabled ? undefined : effectiveIsValid}
                required={required}
                ref={formgroupRef}
                {...rest}
            >
                {renderInput({
                    getInputProps: getInputProps as <T>(oriProps: FieldProps<T>) => FieldProps<T>,
                    markFieldDirty: markFieldDirty as (value?: string, options?: MarkFieldDirtyOptions) => void,
                })}
                {renderLabelIcons()}
                {renderHelperText()}
            </FlosFormGroup>
        );
    }
);

FlosField.displayName = 'FlosField';

const TypeaheadFlosField = React.forwardRef<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    Omit<FlosFieldProps & FlosFieldInternalProps, 'variant' | 'custom' | 'editable'>
>((props, ref) => {
    return <FlosField {...props} variant="typeahead" ref={ref} />;
});

TypeaheadFlosField.displayName = 'TypeaheadFlosField';

export type { FlosFieldProps, FlosFieldInternalProps, FlosFieldRenderInputProps, ValidationState };
export { FlosField, TypeaheadFlosField };
export default FlosField;
